import { signOut as firebaseSignOut, onAuthStateChanged, signInWithPopup } from 'firebase/auth'
import { create } from 'zustand'

import type { IAuthState } from '@/definitions/auth'
import type { IUser } from '@/definitions/user'
import { auth, googleProvider } from '@/services/firebase'

export type { IUser } from '@/definitions/user'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/popup-closed-by-user': 'Sign-in cancelled. Please try again.',
  'auth/popup-blocked': 'Popup blocked. Please allow popups for this site.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/user-disabled': 'This account has been disabled.',
}

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code
    if (AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code]
  }
  if (error instanceof Error) return error.message
  return 'An error occurred during sign-in.'
}

export const useAuthStore = create<IAuthState>()((set) => ({
  user: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  signInWithGoogle: async () => {
    set({ error: null, isLoading: true })
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const email = result.user.email ?? ''
      if (!email.endsWith('@barti.com') && !email.endsWith('@getbarti.com')) {
        await firebaseSignOut(auth)
        throw new Error('Access restricted to @barti.com and @getbarti.com accounts')
      }
      const token = await result.user.getIdToken()
      const user: IUser = {
        uid: result.user.uid,
        email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      }
      set({ user, idToken: token, isAuthenticated: true, isLoading: false })
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
    }
  },

  signOut: async () => {
    try {
      await firebaseSignOut(auth)
      set({ user: null, idToken: null, isAuthenticated: false, error: null })
    } catch (error) {
      set({ error: getErrorMessage(error) })
    }
  },

  initializeAuth: () => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        const email = user.email ?? ''
        if (!email.endsWith('@barti.com') && !email.endsWith('@getbarti.com')) {
          await firebaseSignOut(auth)
          set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
          return
        }
        try {
          const token = await user.getIdToken()
          const mappedUser: IUser = { uid: user.uid, email, displayName: user.displayName, photoURL: user.photoURL }
          set({ user: mappedUser, idToken: token, isAuthenticated: true, isLoading: false })
        } catch {
          set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
        }
      } else {
        set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
      }
    })
  },

  refreshToken: async () => {
    const currentUser = auth.currentUser
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken(true)
        set({ idToken: token })
      } catch {
        useAuthStore.getState().signOut()
      }
    }
  },
}))
